import React, { useRef, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import InfiniteScroll from "react-infinite-scroll-component";
import { observer } from "mobx-react";

import classNames from "classnames";

import { useRootAppStore } from "@hooks";
import {
	ChatReplyScrollingLoader,
	MessageContainer,
} from "@features/chat/mobile";
import { AmityMessageMutated } from "@state/stores/chat/ChatChannelHandler";
import {
	ActiveSharedBetSlip,
	ChatMessageType,
	ExpandedMessageObject,
} from "@data-types/chat";
import { chatScrollToBottom } from "@utils/chat";
import ErrorBoundaryWithPropagation from "@components/boundary/ErrorBoundaryWithPropagation";
import { SharedLoaderSpinner } from "@components/loader/shared";

export default observer(function ChatMessagesWrapper() {
	const {
		chatViewStore: {
			chatChannelHandler,
			replyScrollingLoader,
			sharedBetSlipData,
			selectedMessageToReplyOn,
		},
	} = useRootAppStore();

	const { t } = useTranslation();

	const chatContainerRef = useRef<HTMLDivElement>(null);
	const messageRef = useRef<HTMLDivElement>(null);
	const scrollingRef = useRef(null);

	const [scrollPosition, setScrollPosition] = useState(0);

	useEffect(() => {
		if (
			chatContainerRef.current &&
			messageRef.current &&
			chatContainerRef.current.scrollTop != 0
		) {
			const messageHeight = messageRef.current.clientHeight;
			chatContainerRef.current.scrollBy({
				top: -messageHeight,
				behavior: "auto",
			});
		}
	}, [chatChannelHandler.messageList.length]);

	useEffect(() => {
		setScrollPosition(0);
	}, [chatChannelHandler.activeChannelInfo]);

	if (!chatChannelHandler.messagesPaginationObject) {
		return null;
	}

	const { onNextPage, hasNextPage } =
		chatChannelHandler.messagesPaginationObject;

	if (onNextPage === undefined || hasNextPage === undefined) {
		return null;
	}

	return (
		<>
			<ErrorBoundaryWithPropagation
				componentName={"Mobile/ChatMessagesWrapper.tsx"}
			>
				<ChatReplyScrollingLoader />

				<div
					ref={chatContainerRef}
					id="scrollableDiv"
					className={classNames("chat__container", {
						"chat__container--scrolling": replyScrollingLoader,
					})}
					aria-disabled={replyScrollingLoader}
				>
					<InfiniteScroll
						ref={scrollingRef}
						className="chat__scroll"
						onScroll={(e: any) => {
							setScrollPosition(e.target.scrollTop);
						}}
						dataLength={chatChannelHandler.messageList.length}
						next={onNextPage}
						hasMore={hasNextPage}
						inverse={true}
						loader={<SharedLoaderSpinner />}
						scrollableTarget="scrollableDiv"
						endMessage={
							<span className="chat__no-more">
								{t("CHAT.NO_MORE_MESSAGES_AVAILABLE")}
							</span>
						}
					>
						<div id="bottom-scroll-div"></div>

						{chatChannelHandler.messageList
							.reverse()
							.map(
								(
									message: ExpandedMessageObject,
									index: number
								) => {
									return (
										<div key={index} ref={messageRef}>
											<MessageContainer
												message={message}
											/>
										</div>
									);
								}
							)}
						<div id="reply-scroll-div"></div>
					</InfiniteScroll>
				</div>

				{scrollPosition < 0 &&
					!replyScrollingLoader &&
					!sharedBetSlipData &&
					!selectedMessageToReplyOn && (
						<button
							type="button"
							className="chat__btn--scroll btn btn--round btn--round--med btn--icon btn--tertiary"
							onClick={() => chatScrollToBottom()}
						>
							<i className="u-icon u-icon--arrow--down u-icon--tny" />
						</button>
					)}
			</ErrorBoundaryWithPropagation>
		</>
	);
});
